import { startOfMonth, endOfMonth, subMonths } from "date-fns";

function tsMs(v) {
  if (v == null) return 0;
  if (typeof v.toMillis === "function") return v.toMillis();
  if (v instanceof Date) return v.getTime();
  if (typeof v === "object" && typeof v.seconds === "number") return v.seconds * 1000;
  if (typeof v === "number" && v > 1e11) return v;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/** Best-effort payment / invoice timestamp for reporting. */
export function getPaymentTimestampMs(doc) {
  if (!doc || typeof doc !== "object") return 0;
  const d = doc;
  const fields = [
    "paid_at",
    "paidAt",
    "created_at",
    "createdAt",
    "invoice_paid_at",
    "completed_at",
    "updated_at",
    "updatedAt",
  ];
  for (const f of fields) {
    const ms = tsMs(d[f]);
    if (ms > 0) return ms;
  }
  return 0;
}

/** Normalize to USD for one-time invoice-style docs (Stripe often stores cents). */
export function paymentAmountUsd(doc) {
  if (!doc || typeof doc !== "object") return 0;
  const d = doc;
  if (typeof d.amount_usd === "number" && Number.isFinite(d.amount_usd)) return d.amount_usd;
  if (typeof d.total_usd === "number" && Number.isFinite(d.total_usd)) return d.total_usd;
  if (d.amount_cents != null) return Number(d.amount_cents) / 100;
  if (d.amount_paid != null) return Number(d.amount_paid) / 100;
  const raw = d.amount ?? d.final_price ?? d.total ?? d.estimated_price;
  if (raw == null) return 0;
  const n = Number(raw);
  if (!Number.isFinite(n)) return 0;
  if (process.env.REACT_APP_CRM_PAYMENT_AMOUNTS_ARE_CENTS === "1") return n / 100;
  return n;
}

const SUCCESS_STATUSES = new Set([
  "succeeded",
  "paid",
  "complete",
  "completed",
  "success",
  "successful",
]);

export function isSuccessfulPayment(doc) {
  if (!doc || typeof doc !== "object") return false;
  const d = doc;
  if (d.refunded === true || d.refund === true) return false;
  const st = d.status != null ? String(d.status).toLowerCase() : "";
  if (st && (st.includes("fail") || st.includes("void") || st.includes("cancel"))) return false;
  if (SUCCESS_STATUSES.has(st)) return true;
  if (d.paid === true || d.is_paid === true) return true;
  if (!st && paymentAmountUsd(d) > 0) return true;
  return false;
}

export function findContactForPayment(contacts, payment) {
  if (!Array.isArray(contacts) || !payment) return null;
  const uid = payment.user_id ?? payment.userId ?? payment.uid ?? payment.firebase_uid ?? payment.firebaseUid;
  const email = (payment.email || payment.customer_email || payment.user_email || "").trim().toLowerCase();
  const suid = uid != null ? String(uid).trim() : "";
  return (
    contacts.find(
      (c) =>
        suid &&
        (String(c.firebaseUid || c.uid || "") === suid ||
          String(c.id) === suid ||
          (c.email && String(c.email).split("@")[0] === suid))
    ) ||
    contacts.find((c) => email && (c.email || "").toLowerCase() === email) ||
    null
  );
}

export function isPaymentExcludedAsInternal(contacts, payment) {
  const c = findContactForPayment(contacts, payment);
  return Boolean(c?.isInternal);
}

/** Sum of all successful payments (excludes internal-attributed). */
export function lifetimeSuccessfulPaymentsUsd(payments, contacts) {
  let sum = 0;
  for (const p of payments) {
    if (!isSuccessfulPayment(p)) continue;
    if (isPaymentExcludedAsInternal(contacts, p)) continue;
    sum += paymentAmountUsd(p);
  }
  return sum;
}

/** Revenue in current calendar month (excludes payments attributed to internal contacts). */
export function revenueThisMonthUsd(payments, contacts) {
  const now = new Date();
  const start = startOfMonth(now).getTime();
  const end = endOfMonth(now).getTime();
  let sum = 0;
  for (const p of payments) {
    if (!isSuccessfulPayment(p)) continue;
    const t = getPaymentTimestampMs(p);
    if (t < start || t > end) continue;
    if (isPaymentExcludedAsInternal(contacts, p)) continue;
    sum += paymentAmountUsd(p);
  }
  return sum;
}

/** Full previous calendar month total. */
export function revenuePreviousMonthUsd(payments, contacts) {
  const ref = subMonths(new Date(), 1);
  const start = startOfMonth(ref).getTime();
  const end = endOfMonth(ref).getTime();
  let sum = 0;
  for (const p of payments) {
    if (!isSuccessfulPayment(p)) continue;
    const t = getPaymentTimestampMs(p);
    if (t < start || t > end) continue;
    if (isPaymentExcludedAsInternal(contacts, p)) continue;
    sum += paymentAmountUsd(p);
  }
  return sum;
}

/**
 * Last `numMonths` months, oldest → newest (for charts).
 * @returns {{ labels: string[], totals: number[], monthStarts: Date[] }}
 */
export function monthlyRevenueSeries(payments, contacts, numMonths = 12) {
  const now = new Date();
  const monthStarts = [];
  for (let i = numMonths - 1; i >= 0; i--) {
    monthStarts.push(startOfMonth(subMonths(now, i)));
  }
  const labels = monthStarts.map((m) =>
    m.toLocaleString("en-US", { month: "short", year: "2-digit" })
  );
  const totals = monthStarts.map((monthStart) => {
    const start = monthStart.getTime();
    const end = endOfMonth(monthStart).getTime();
    return payments.reduce((sum, p) => {
      if (!isSuccessfulPayment(p)) return sum;
      const t = getPaymentTimestampMs(p);
      if (t < start || t > end) return sum;
      if (isPaymentExcludedAsInternal(contacts, p)) return sum;
      return sum + paymentAmountUsd(p);
    }, 0);
  });
  return { labels, totals, monthStarts };
}

/**
 * Paid customers: non-internal contacts with stage paid_customer OR at least one successful payment
 * attributed to that contact.
 */
export function paidContactIdSet(contacts, payments) {
  const ids = new Set();
  for (const c of contacts) {
    if (c.isInternal) continue;
    if (c.stage === "paid_customer") ids.add(c.id);
  }
  for (const p of payments) {
    if (!isSuccessfulPayment(p)) continue;
    const c = findContactForPayment(contacts, p);
    if (c && !c.isInternal) ids.add(c.id);
  }
  return ids;
}

export function countPaidCustomers(contacts, payments) {
  return paidContactIdSet(contacts, payments).size;
}

function countDocsInRange(docs, getTimeMs, startMs, endMs) {
  let n = 0;
  for (const d of docs) {
    const t = getTimeMs(d);
    if (t >= startMs && t <= endMs) n++;
  }
  return n;
}

function docCreatedMs(d) {
  return tsMs(d.createdAt ?? d.created_at ?? d.created ?? d.timestamp);
}

/** Aggregate numbers for Dashboard + Analytics + custom formulas. */
export function buildMetricVariables({
  contacts = [],
  payments = [],
  orders = [],
  scans = [],
  workspaces = [],
  workspaceMembers = [],
}) {
  const now = Date.now();
  const d30 = now - 30 * 86400000;
  const yStart = new Date(new Date().getFullYear(), 0, 1).getTime();

  const contactsNonInternal = contacts.filter((c) => !c.isInternal);
  const paidCustomers = countPaidCustomers(contacts, payments);

  const revMtd = revenueThisMonthUsd(payments, contacts);
  const revPrev = revenuePreviousMonthUsd(payments, contacts);
  let revYtd = 0;
  for (const p of payments) {
    if (!isSuccessfulPayment(p)) continue;
    const t = getPaymentTimestampMs(p);
    if (t < yStart || t > now) continue;
    if (isPaymentExcludedAsInternal(contacts, p)) continue;
    revYtd += paymentAmountUsd(p);
  }

  const orders30 = countDocsInRange(orders, docCreatedMs, d30, now);
  const scans30 = countDocsInRange(scans, docCreatedMs, d30, now);
  const ws30 = countDocsInRange(workspaces, docCreatedMs, d30, now);

  const mStart = startOfMonth(new Date()).getTime();
  const mEnd = endOfMonth(new Date()).getTime();
  const ordersMtd = countDocsInRange(orders, docCreatedMs, mStart, mEnd);

  const paymentsMtd = payments.filter((p) => {
    if (!isSuccessfulPayment(p)) return false;
    const t = getPaymentTimestampMs(p);
    return t >= startOfMonth(new Date()).getTime() && t <= endOfMonth(new Date()).getTime();
  }).length;

  const { totals: monthTotals } = monthlyRevenueSeries(payments, contacts, 12);
  const vars = {
    revenue_mtd: revMtd,
    revenue_prev_month: revPrev,
    revenue_ytd: revYtd,
    paid_customers: paidCustomers,
    contacts_total: contactsNonInternal.length,
    contacts_all: contacts.length,
    orders_30d: orders30,
    scans_30d: scans30,
    workspaces_total: workspaces.length,
    workspaces_30d: ws30,
    workspace_members_total: workspaceMembers.length,
    payments_count_mtd: paymentsMtd,
    orders_mtd: ordersMtd,
  };

  for (let i = 0; i < monthTotals.length; i++) {
    vars[`revenue_m${i}`] = monthTotals[i];
  }
  const lastIdx = monthTotals.length - 1;
  if (lastIdx >= 0) {
    vars.revenue = monthTotals[lastIdx];
    vars.r = monthTotals[lastIdx];
  }

  return vars;
}
